import React, { useContext, useState, useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button, Text, Link } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { TileHeader } from '@bingads-webui-campaign-react/goals-and-website-tile';
import {
  CampaignCreationMethod,
  CampaignCreationMethodSelection,
} from '@bingads-webui-campaign-react/campaign-creation-method-selection';

import { WebsitePromptInputs } from '../shared';
import { getCampaignCreationMethodSectionStyles } from '../styles/campaign-creation-method-section-styles';

export function CampaignCreationMethodSection({
  website,
  setWebsite,
  websiteInputRef,
  methodsContainerRef,
  setIsValid,
  alwaysShowError,
  displayMode,
  onClickCreatePreviewAds,
  onClickStartImportGoogle,
  onClickStartImportFacebook,
  logger,
}) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCampaignCreationMethodSectionStyles);
  const [creationMethod, setCreationMethod] = useState(CampaignCreationMethod.NEW_CAMPAIGN);

  const onClickCreateNewCampaign = useCallback(() => {
    logger.trace('[DiscoverSection] User selected to create a new campaign');
    setCreationMethod(CampaignCreationMethod.NEW_CAMPAIGN);
  }, [logger]);

  const onClickGoogleImport = useCallback(() => {
    logger.trace('[DiscoverSection] User selected to import campaigns from Google Ads');
    setCreationMethod(CampaignCreationMethod.GOOGLE_IMPORT);
  }, [logger]);

  const onClickFacebookImport = useCallback(() => {
    logger.trace('[DiscoverSection] User selected to import campaigns from Meta Ads');
    setCreationMethod(CampaignCreationMethod.FACEBOOK_IMPORT);
  }, [logger]);

  const onClickCreateNow = useCallback(() => {
    if (creationMethod === CampaignCreationMethod.NEW_CAMPAIGN) {
      logger.trace('[DiscoverSection] Create now clicked to preview ads');
      onClickCreatePreviewAds();
    } else if (creationMethod === CampaignCreationMethod.GOOGLE_IMPORT) {
      logger.trace('[DiscoverSection] Create now clicked to start Google import');
      onClickStartImportGoogle();
    } else if (creationMethod === CampaignCreationMethod.FACEBOOK_IMPORT) {
      logger.trace('[DiscoverSection] Create now clicked to start Facebook import');
      onClickStartImportFacebook();
    }
  }, [creationMethod, logger, onClickCreatePreviewAds, onClickStartImportFacebook, onClickStartImportGoogle]);

  const ImportHelpLink = creationMethod === CampaignCreationMethod.GOOGLE_IMPORT ?
    'https://help.ads.microsoft.com/#apex/ads/en/50851/0' : 'https://help.ads.microsoft.com/#apex/ads/en/60077/0';

  return (
    <React.Fragment>
      <div
        className={classNames(
          'creation-method-selection-container',
          classes.creationMethodSelectionContainer,
          classes[`creationMethodSelectionContainer-${displayMode}`]
        )}
        ref={methodsContainerRef}
      >
        <TileHeader
          title={i18n.getString(_TL_('How would you like to start your campaign?'))}
          displayMode={displayMode}
          hideIcon
        />
        <CampaignCreationMethodSelection
          i18n={i18n}
          onClickCreateNewCampaign={onClickCreateNewCampaign}
          onClickGoogleImport={onClickGoogleImport}
          onClickFacebookImport={onClickFacebookImport}
          isVertical={displayMode === 's' || displayMode === 'm'}
          disableImports={false}
          selectedItem={creationMethod}
          isRebranding
        />
        {creationMethod === CampaignCreationMethod.NEW_CAMPAIGN && (
          <WebsitePromptInputs
            website={website}
            setWebsite={setWebsite}
            websiteInputRef={websiteInputRef}
            setIsValid={setIsValid}
            alwaysShowError={alwaysShowError}
            showPrompt={false}
          />
        )}
        {(creationMethod === CampaignCreationMethod.GOOGLE_IMPORT ||
          creationMethod === CampaignCreationMethod.FACEBOOK_IMPORT) && (
            <div className={classNames('import-text-container', classes.importTextContainer)}>
              <Text variant="medium" className="import-text" block>
                {i18n.getString(_TL_('Get your campaigns up and running quickly by importing them directly from {{platform}} Ads. Before you start, make sure you understand'), { platform: creationMethod === CampaignCreationMethod.GOOGLE_IMPORT ? 'Google' : 'Meta' })}&nbsp;
                <Link href={ImportHelpLink} target="_blank" rel="noopener noreferrer" className="import-help-link">
                  {i18n.getString(_TL_('What will get imported'))}
                </Link>
              </Text>
            </div>
          )}
      </div>
      {creationMethod &&
        <Button
          className="create-now-button"
          onClick={onClickCreateNow}
          appearance="primary"
        >
          {creationMethod === CampaignCreationMethod.NEW_CAMPAIGN && i18n.getString(_TL_('Preview my ads'))}
          {creationMethod === CampaignCreationMethod.GOOGLE_IMPORT && i18n.getString(_TL_('Start import'))}
          {creationMethod === CampaignCreationMethod.FACEBOOK_IMPORT && i18n.getString(_TL_('Start import'))}
        </Button>}
    </React.Fragment>
  );
}

CampaignCreationMethodSection.propTypes = {
  website: PropTypes.string.isRequired,
  setWebsite: PropTypes.func.isRequired,
  websiteInputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  methodsContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  setIsValid: PropTypes.func.isRequired,
  alwaysShowError: PropTypes.bool.isRequired,
  onClickCreatePreviewAds: PropTypes.func.isRequired,
  onClickStartImportGoogle: PropTypes.func.isRequired,
  onClickStartImportFacebook: PropTypes.func.isRequired,
  displayMode: PropTypes.string,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

CampaignCreationMethodSection.defaultProps = {
  websiteInputRef: undefined,
  methodsContainerRef: undefined,
  displayMode: 'l',
  logger: {
    trace: _.noop,
  },
};
